import {KubernetesAlert, kubernetesEvents} from "../../pages/alerts/KubernetesAlert";
import {LogAlert, LogEventRegexMatchPayload, logEvents} from "../../pages/alerts/LogAlert";
import {
    AggregationFunction,
    EvalType,
    MetricAlert,
    MissingDatapointBehavior,
    WindowUnit
} from "../../pages/alerts/MetricAlert";
import {TraceAlert} from "../../pages/alerts/TraceAlert";
import {KubernetesResourceAlert} from "../../pages/alerts/KubernetesResourceAlert";
import {MultiMetricAlert} from "../../pages/alerts/MultiMetricAlert";
import {plainToClassFromExist} from "class-transformer";
import { timeAggregations} from "../../pages/alerts/utils";
import {
    AlertDestination,
    AlertDestinationType,
    EmailAlertDestination, GuardianInvestigationAlertDestination,
    PagerDutyAlertDestination,
    SlackAlertDestination,
    WebhookAlertDestination
} from "./AlertDestination";
import {MetoroQLQuerySpecifier, MetricSpecifier} from "../../clients/metoro/metrics";
import {MetricFunction} from "../Dashboarding/widgets/MetricSelector";
import {Formula} from "../../types/formula";
import {Alert, AlertType, ThresholdComparator} from "../../pages/alerts/model";

export const nanoToMilliSeconds = 1_000_000

interface ApiServerAlert {
    status?: string
    muted: boolean
    uuid: string
    name: string
    description: string
    alertManagement?: {
        managingResourceType?: string,
        managingResourceIdentifier?: string,
        // Unix seconds timestamp
        lastSyncedTime?: number,
    },
    type: AlertType
    destinations: {
        type: AlertDestinationType
        slackDestination?: SlackAlertDestination
        pagerDutyDestination?: PagerDutyAlertDestination
        emailDestination?: EmailAlertDestination
        webhookDestination?: WebhookAlertDestination
    }[],
    metricAlert?: {
        filters: {
            metricName: string,
            filters: Map<string, string[]>,
            excludeFilters: Map<string, string[]>,
            splits: string[],
            aggregation: string,
            bucketSize: number,
            functions: MetricFunction[]
        },
        monitorEvaluation: {
            description: string,
            monitorEvaluationType: EvalType,
            monitorEvalutionPayload: {
                // Following fields are for evaluation type aggregation.
                evaluationFunction: AggregationFunction
                window: number,
                windowUnit: WindowUnit,
                evaluationSplits: string[]
                // Following fields are for evaluation type static.
                evaluationWindow: number,
                datapointsToAlarm: number,
                missingDatapointBehavior: MissingDatapointBehavior
            },
            anomalyCondition?: {
                algorithm: string,
                zScoreParameters?: {
                    operator: string,
                    threshold: number,
                    dataPointsInBaselineWindow: number,
                    dataPointsInEvaluationWindow: number,
                    dataPointsToAlarm: number
                }
            }
        },
        alarmCondition: {
            condition: ThresholdComparator,
            threshold: string
        },
    },
    traceAlert?: {
        filters: {
            filters: Map<string, string[]>,
            excludeFilters: Map<string, string[]>,
            splits: string[],
            aggregation: string,
            bucketSize: number,
            functions: MetricFunction[]
        },
        monitorEvaluation: {
            description: string,
            monitorEvaluationType: EvalType,
            monitorEvalutionPayload: {
                // Following fields are for evaluation type aggregation.
                evaluationFunction: AggregationFunction
                window: number,
                windowUnit: WindowUnit,
                evaluationSplits: string[]
                // Following fields are for evaluation type static.
                evaluationWindow: number,
                datapointsToAlarm: number,
                missingDatapointBehavior: MissingDatapointBehavior
            },
            anomalyCondition?: {
                algorithm: string,
                zScoreParameters?: {
                    operator: string,
                    threshold: number,
                    dataPointsInBaselineWindow: number,
                    dataPointsInEvaluationWindow: number,
                    dataPointsToAlarm: number
                }
            }
        },
        alarmCondition: {
            condition: ThresholdComparator,
            threshold: string
        },
    },
    kubernetesResourceAlert?: {
        filters: {
            filters: Map<string, string[]>,
            excludeFilters: Map<string, string[]>,
            splits: string[],
            aggregation: string,
            bucketSize: number,
            functions: MetricFunction[]
            jsonPath: string
        },
        monitorEvaluation: {
            description: string,
            monitorEvaluationType: EvalType,
            monitorEvalutionPayload: {
                // Following fields are for evaluation type aggregation.
                evaluationFunction: AggregationFunction
                window: number,
                windowUnit: WindowUnit,
                evaluationSplits: string[]
                // Following fields are for evaluation type static.
                evaluationWindow: number,
                datapointsToAlarm: number,
                missingDatapointBehavior: MissingDatapointBehavior
            },
            anomalyCondition?: {
                algorithm: string,
                zScoreParameters?: {
                    operator: string,
                    threshold: number,
                    dataPointsInBaselineWindow: number,
                    dataPointsInEvaluationWindow: number,
                    dataPointsToAlarm: number
                }
            }
        },
        alarmCondition: {
            condition: ThresholdComparator,
            threshold: string
        },
    },
    kubernetesAlert?: {
        filters: {
            services: string[],
            clusters: string[]
        },
        monitorEvent: string,
        scope: {
            service: boolean,
            environment: boolean
        }
    },
    logAlert?: {
        filters: {
            services: string[],
            clusters: string[]
        },
        monitorEvent: string,
        logAlertRegexPayload?: LogEventRegexMatchPayload
    },
    multiMetricAlert?: {
        filters: {
            metricSpecifiers: MetricSpecifier[],
            formula?: Formula,
            metoroQlQuery?: MetoroQLQuerySpecifier
        },
        monitorEvaluation: {
            description: string,
            monitorEvaluationType: EvalType,
            monitorEvalutionPayload: {
                // Following fields are for evaluation type aggregation.
                evaluationFunction: AggregationFunction
                window: number,
                windowUnit: WindowUnit,
                evaluationSplits: string[]
                // Following fields are for evaluation type static.
                evaluationWindow: number,
                datapointsToAlarm: number,
                missingDatapointBehavior: MissingDatapointBehavior
            },
            anomalyCondition?: {
                algorithm: string,
                zScoreParameters?: {
                    operator: string,
                    threshold: number,
                    dataPointsInBaselineWindow: number,
                    dataPointsInEvaluationWindow: number,
                    dataPointsToAlarm: number
                }
            }
        },
        alarmCondition: {
            condition: ThresholdComparator,
            threshold: string
        },
    }
}

export function deserializeAlert(alert: any): Alert {
    let alertInner = {} as KubernetesAlert | LogAlert | MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert
    if (alert.kubernetesAlert !== null && alert.kubernetesAlert !== undefined) {
        alertInner = {
            filters: alert.kubernetesAlert.filters,
            scope: alert.kubernetesAlert.scope,
            monitorEvent: kubernetesEvents.find((event) => event.id === alert.kubernetesAlert.monitorEvent) || kubernetesEvents[0]
        } as KubernetesAlert
    }
    if (alert.logAlert !== null && alert.logAlert !== undefined) {
        alertInner = {
            filters: alert.logAlert.filters,
            monitorEvent: logEvents.find((event) => event.id === alert.logAlert.monitorEvent) || logEvents[0],
            eventPayload: alert.logAlert.logAlertRegexPayload
        } as LogAlert
    }

    if (alert.metricAlert !== null && alert.metricAlert !== undefined) {
        let filterMap = new Map<string, string[]>()
        plainToClassFromExist(filterMap, alert.metricAlert.filters.filters)
        let excludeFilterMap = new Map<string, string[]>()
        plainToClassFromExist(excludeFilterMap, alert.metricAlert.filters.excludeFilters || {})

        alertInner = {
            filters: {
                filters: filterMap,
                metricName: alert.metricAlert.filters.metricName,
                excludeFilters: excludeFilterMap,
                splits: alert.metricAlert.filters.splits,
                aggregation: alert.metricAlert.filters.aggregation,
                bucketSize: alert.metricAlert.filters.bucketSize,
                functions: alert.metricAlert.filters.functions
            },
            monitorEvaluation: alert.metricAlert.monitorEvaluation,
            eventPayload: alert.metricAlert.alarmCondition
        } as MetricAlert
    }

    if (alert.multiMetricAlert !== null && alert.multiMetricAlert !== undefined) {
        alertInner = {
            filters: {
                metricSpecifiers: alert.multiMetricAlert.filters.metricSpecifiers.map((spec: any) => {
                    // Check the type of filters is a map, if not, convert it to a map
                    if (!(spec.filters instanceof Map)) {
                        let filterMap = new Map<string, string[]>()
                        plainToClassFromExist(filterMap, spec.filters)
                        spec.filters = filterMap
                    }
                    if (!(spec.excludeFilters instanceof Map)) {
                        let excludeFilterMap = new Map<string, string[]>()
                        plainToClassFromExist(excludeFilterMap, spec.excludeFilters)
                        spec.excludeFilters = excludeFilterMap
                    }

                    return {
                        ...spec,
                    }
                }),
                metoroQlQuery: alert.multiMetricAlert.filters.metoroQlQuery,
                formula: alert.multiMetricAlert.filters.formula
            },
            monitorEvaluation: alert.multiMetricAlert.monitorEvaluation,
            eventPayload: {
                condition: alert.multiMetricAlert.alarmCondition.condition,
                threshold: alert.multiMetricAlert.alarmCondition.threshold
            }
        } as MultiMetricAlert
    }

    if (alert.traceAlert !== null && alert.traceAlert !== undefined) {
        let filterMap = new Map<string, string[]>()
        plainToClassFromExist(filterMap, alert.traceAlert.filters.filters)
        let excludeFilterMap = new Map<string, string[]>()
        plainToClassFromExist(excludeFilterMap, alert.traceAlert.filters.excludeFilters || {})
        alertInner = {
            filters: {
                filters: filterMap,
                excludeFilters: excludeFilterMap,
                splits: alert.traceAlert.filters.splits,
                aggregation: alert.traceAlert.filters.aggregation,
                bucketSize: alert.traceAlert.filters.bucketSize,
                functions: alert.traceAlert.filters.functions
            },
            monitorEvaluation: alert.traceAlert.monitorEvaluation,
            eventPayload: {
                condition: alert.traceAlert.alarmCondition.condition,
                threshold: alert.traceAlert.alarmCondition.threshold
            }
        } as TraceAlert
    }

    if (alert.kubernetesResourceAlert !== null && alert.kubernetesResourceAlert !== undefined) {
        let filterMap = new Map<string, string[]>()
        plainToClassFromExist(filterMap, alert.kubernetesResourceAlert.filters.filters)
        let excludeFilterMap = new Map<string, string[]>()
        plainToClassFromExist(excludeFilterMap, alert.kubernetesResourceAlert.filters.excludeFilters || {})
        alertInner = {
            filters: {
                filters: filterMap,
                excludeFilters: excludeFilterMap,
                splits: alert.kubernetesResourceAlert.filters.splits,
                aggregation: alert.kubernetesResourceAlert.filters.aggregation,
                bucketSize: alert.kubernetesResourceAlert.filters.bucketSize,
                functions: alert.kubernetesResourceAlert.filters.functions,
                jsonPath: alert.kubernetesResourceAlert.filters.jsonPath
            },
            monitorEvaluation: alert.kubernetesResourceAlert.monitorEvaluation,
            eventPayload: alert.kubernetesResourceAlert.alarmCondition
        } as KubernetesResourceAlert
    }

    let alertDestinations = [] as Array<AlertDestination>

    alertDestinations = alert.destinations.map((destination: any) => {
        let alertDestination = {} as SlackAlertDestination | PagerDutyAlertDestination | EmailAlertDestination | WebhookAlertDestination | GuardianInvestigationAlertDestination

        if (destination.type === AlertDestinationType.Slack && destination.slackDestination) {
            alertDestination = {
                channel: destination.slackDestination.channel,
                additionalMessage: destination.slackDestination.additionalMessage
            } as SlackAlertDestination
        } else if (destination.type === AlertDestinationType.PagerDuty && destination.pagerDutyDestination) {
            alertDestination = {
                serviceName: destination.pagerDutyDestination.serviceName,
                serviceId: destination.pagerDutyDestination.serviceId
            } as PagerDutyAlertDestination
        } else if (destination.type === AlertDestinationType.Email && destination.emailDestination) {
            alertDestination = {
                emails: destination.emailDestination.emails
            } as EmailAlertDestination
        } else if (destination.type === AlertDestinationType.Webhook && destination.webhookDestination) {
            alertDestination = {
                uuid: destination.webhookDestination.uuid,
                name: destination.webhookDestination.name
            } as WebhookAlertDestination
        } else if (destination.type === AlertDestinationType.GuardianInvestigation && destination.guardianInvestigationDestination) {
            alertDestination = {
            } as GuardianInvestigationAlertDestination;
        }

        return {
            type: destination.type,
            alertDestination: alertDestination
        }
    })

    return {
        uuid: alert.uuid,
        type: alert.type,
        name: alert.name,
        description: alert.description,
        alert: alertInner,
        destinations: alertDestinations
    };
}

export function convertToApiServerAlert(alert: Alert): any {
    let destinations = alert.destinations.map(dest => {
        let destination = {}

        if (dest.type === AlertDestinationType.Slack) {
            destination = {
                type: dest.type,
                slackDestination: {
                    channel: (dest.alertDestination as SlackAlertDestination).channel,
                    additionalMessage: (dest.alertDestination as SlackAlertDestination).additionalMessage
                }
            }
        }
        if (dest.type === AlertDestinationType.PagerDuty) {
            destination = {
                type: dest.type,
                pagerDutyDestination: {
                    serviceName: (dest.alertDestination as PagerDutyAlertDestination).serviceName,
                    serviceId: (dest.alertDestination as PagerDutyAlertDestination).serviceId
                }
            }
        }
        if (dest.type === AlertDestinationType.Email) {
            destination = {
                type: dest.type,
                emailDestination: {
                    emails: (dest.alertDestination as EmailAlertDestination).emails
                }
            }
        }
        if (dest.type === AlertDestinationType.Webhook) {
            destination = {
                type: dest.type,
                webhookDestination: {
                    uuid: (dest.alertDestination as WebhookAlertDestination).uuid,
                    name: (dest.alertDestination as WebhookAlertDestination).name
                }
            }
        }
        if (dest.type === AlertDestinationType.GuardianInvestigation) {
            destination = {
                type: dest.type,
                guardian: {
                    uuid: (dest.alertDestination as WebhookAlertDestination).uuid,
                    name: (dest.alertDestination as WebhookAlertDestination).name
                }
            }
        }
        return destination
    })

    let apiServerAlert: any = {
        uuid: alert.uuid,
        name: alert.name,
        description: alert.description,
        type: alert.type,
        destinations: destinations
    }

    if (alert.type === AlertType.Kubernetes) {
        const kubernetesAlert = alert.alert as KubernetesAlert;
        const filters = kubernetesAlert.filters as { services: string[], clusters: string[] };
        apiServerAlert.kubernetesAlert = {
            filters: {
                services: filters.services,
                clusters: filters.clusters
            },
            monitorEvent: kubernetesAlert.monitorEvent.id,
            scope: {
                service: kubernetesAlert.scope.service,
                environment: kubernetesAlert.scope.environment
            }
        }
    }

    if (alert.type === AlertType.Log) {
        const logAlert = alert.alert as LogAlert;
        const filters = logAlert.filters as { services: string[], clusters: string[] };
        apiServerAlert.logAlert = {
            filters: {
                services: filters.services,
                clusters: filters.clusters
            },
            monitorEvent: logAlert.monitorEvent.id,
            logAlertRegexPayload: logAlert.eventPayload
        }
    }

    if (alert.type === AlertType.Metric) {
        const metricAlert = alert.alert as MetricAlert;
        apiServerAlert.metricAlert = {
            filters: {
                filters: Object.fromEntries(metricAlert.filters.filters),
                excludeFilters: Object.fromEntries(metricAlert.filters.excludeFilters || new Map()),
                splits: metricAlert.filters.splits,
                metricName: metricAlert.filters.metricName,
                functions: metricAlert.filters.functions,
                aggregation: metricAlert.filters.aggregation,
                bucketSize: metricAlert.filters.bucketSize
            },
            monitorEvaluation: metricAlert.monitorEvaluation,
            alarmCondition: metricAlert.eventPayload
        }
    }

    if (alert.type === AlertType.Trace) {
        const traceAlert = alert.alert as TraceAlert;
        apiServerAlert.traceAlert = {
            filters: {
                filters: Object.fromEntries(traceAlert.filters.filters),
                excludeFilters: Object.fromEntries(traceAlert.filters.excludeFilters || new Map()),
                splits: traceAlert.filters.splits,
                aggregation: traceAlert.filters.aggregation,
                bucketSize: traceAlert.filters.bucketSize,
                functions: traceAlert.filters.functions
            },
            monitorEvaluation: traceAlert.monitorEvaluation,
            alarmCondition: {
                condition: traceAlert.eventPayload.condition,
                threshold: Number(getThreshold(alert))
            }
        }
    }

    if (alert.type === AlertType.KubernetesResource) {
        const kubernetesResourceAlert = alert.alert as KubernetesResourceAlert;
        apiServerAlert.kubernetesResourceAlert = {
            filters: {
                filters: Object.fromEntries(kubernetesResourceAlert.filters.filters),
                excludeFilters: Object.fromEntries(kubernetesResourceAlert.filters.excludeFilters || new Map()),
                splits: kubernetesResourceAlert.filters.splits,
                aggregation: kubernetesResourceAlert.filters.aggregation,
                bucketSize: kubernetesResourceAlert.filters.bucketSize,
                functions: kubernetesResourceAlert.filters.functions,
                jsonPath: kubernetesResourceAlert.filters.jsonPath
            },
            monitorEvaluation: kubernetesResourceAlert.monitorEvaluation,
            alarmCondition: kubernetesResourceAlert.eventPayload
        }
    }

    if (alert.type === AlertType.MultiMetric) {
        const multiMetricAlert = alert.alert as MultiMetricAlert;
        apiServerAlert.multiMetricAlert = {
            filters: {
                metricSpecifiers: multiMetricAlert.filters.metricSpecifiers.map(spec => {
                    const baseSpec = {
                        metricName: spec.metricName,
                        metricType: spec.metricType,
                        filters: Object.fromEntries(spec.filters || new Map()),
                        excludeFilters: Object.fromEntries(spec.excludeFilters || new Map()),
                        regexes: spec.regexes || [],
                        excludeRegexes: spec.excludeRegexes || [],
                        splits: spec.splits || [],
                        aggregation: spec.aggregation,
                        bucketSize: spec.bucketSize,
                        functions: spec.functions || [],
                        jsonPath: spec.jsonPath,
                        shouldNotReturn: spec.shouldNotReturn,
                        transforms: spec.transforms || [],
                    } as MetricSpecifier;

                    return baseSpec;
                }),
                formula: multiMetricAlert.filters.formula,
                metoroQlQuery: multiMetricAlert.filters.metoroQlQuery
            },
            monitorEvaluation: multiMetricAlert.monitorEvaluation,
            alarmCondition: {
                condition: multiMetricAlert.eventPayload.condition,
                threshold: Number(getThreshold(alert))
            }
        }
    }

    return {
        alert: apiServerAlert
    }
}

function getThreshold(alert: Alert): number {
    if (alert.type === AlertType.Trace) {
        return (alert.alert as TraceAlert).eventPayload.threshold
    } else if (alert.type === AlertType.MultiMetric && (alert.alert as MultiMetricAlert).filters.metricSpecifiers.length === 1 && (alert.alert as MultiMetricAlert).filters.metricSpecifiers[0].metricType === "trace") {
        return (alert.alert as MultiMetricAlert).eventPayload.threshold
    }
    return (alert.alert as MetricAlert | TraceAlert | KubernetesResourceAlert | MultiMetricAlert).eventPayload.threshold
}

export type {ApiServerAlert}